import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SelectChangeEvent, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { hideLoader, showLoader } from '@utils/Loader';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import useOpenModal from '@hooks/useOpenModal';
import usePosCart from '@screens/POS/hooks/usePosCart';
import usePosSessionMirror from '@screens/POS/hooks/usePosSessionMirror';
import { RootReducer } from '@store/index';
import { setCashRegister } from '@store/krispisStore';
import { isActiveCashRegister } from '@utils/CashRegisters';
import { Order, OrderStatus, isTableOrder } from '@api/Orders/Order';
import { buildCreatedWith, buildOrderPayments } from '@api/Orders/OrderPayments';
import { needsPaymentBeforeComplete } from '@api/Orders/OrderPaymentGate';
import { reserveShortId } from '@api/Orders/ShortId';
import OrdersService from '@api/Orders/OrderCtrl';
import RewardsService from '@api/Rewards/RewardCtrl';
import { Reward } from '@api/Rewards/Reward';
import CashRegisterService from '@api/CashRegister/CashRegisterCtrl';
import BranchOfficesService from '@api/BranchOffices/BranchOfficeCtrl';
import TableKioskService from '@api/TablesKiosks/TableKioskCtrl';
import { TableKiosk } from '@api/TablesKiosks/TableKiosk';
import { SalesChannel } from '@api/SalesChannels/SalesChannel';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';
import { Dish } from '@api/Dishes/Dish';
import { Recipe } from '@api/Recipes/Recipe';
import { ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import { Input } from '@api/Inputs/Input';
import { getCatalogAvailability } from '@api/Catalog/Availability';
import { ShoppingCartLineV2, ShoppingCartV2 } from '@api/ShoppingCart/ShoppingCartV2';
import SavedOrdersService from '@api/SavedOrders/SavedOrderCtrl';
import { SavedOrder } from '@api/SavedOrders/SavedOrder';

import DishesGrid from '@screens/POS/components/DishesGrid';
import { PosOrderPanel } from '@screens/POS/components/PosOrderPanel';
import { PosModals } from '@screens/POS/components/PosModals';
import ProductCategoriesGrid from '@screens/POS/components/ProductCategoriesGrid';
import { getPosCatalog } from '@api/Catalog/CatalogCache';

/**
 * EL PUNTO DE VENTA. Vende desde el catálogo (`dishes`) y escribe en `orders`.
 *
 * Se llamó "POS 2.0" mientras convivió con el POS viejo; ése se eliminó en el
 * cutover del 2026-08-17 y esta pantalla pasó a ser la única, así que en
 * 2026-08-27 dejó de llamarse POS2Screen. **La distinción que SÍ sigue viva es la
 * de las LÍNEAS**: aquí se escriben líneas v2 (`OrderLineV2`), pero las órdenes
 * históricas traen líneas v1 y por eso el renderer de `@utils/Order` es dual.
 *
 * Qué hace: caja obligatoria, cliente walk-in o registrado, canal de venta o mesa,
 * canje de puntos, pago libre en parcialidades, folio atómico y cierre de mesa.
 * Propio suyo son el grid desde `dishes`, el modal de personalización que aplica
 * min/max/obligatorio/repetir de verdad, la marca de agotados por sucursal (§4.3)
 * y los pedidos guardados (`saved-orders` con `schemaVersion: 2`).
 *
 * La orden creada aquí entra sola al tablero de /orders, que es lo que ve cocina.
 */
/**
 * Con qué build nació la orden (F3 del PLAN-MODELO-PAGOS-Y-ORIGEN-ORDENES).
 * `REACT_APP_VERSION` es la misma que publica `version.json`, así que se puede
 * cruzar contra la versión desplegada para detectar tablets rezagadas.
 */
const POS_CREATED_WITH = buildCreatedWith('admin', 'pos', process.env.REACT_APP_VERSION);

const POSScreen: React.FunctionComponent = () => {
    const { loggedUser, branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { cashRegister, salesChannels } = useSelector((state: RootReducer) => state.krispis);
    const location = useLocation();
    const navigation = useNavigate();
    const dispatch = useDispatch();

    // Services
    const ordersService = useMemo(() => new OrdersService(), []);
    const branchOfficesService = useMemo(() => new BranchOfficesService(), []);
    const rewardsService = useMemo(() => new RewardsService(), []);
    const cashRegisterService = useMemo(() => new CashRegisterService(), []);
    const tableKioskService = useMemo(() => new TableKioskService(), []);
    const savedOrdersService = useMemo(() => new SavedOrdersService(), []);

    // Catálogo 2.0
    const [dishes, setDishes] = useState<Dish[]>([]);
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);
    const [inputs, setInputs] = useState<Input[]>([]);
    const [categories, setCategories] = useState<ProductCategory[]>([]);
    const [unavailableInputIds, setUnavailableInputIds] = useState<string[]>([]);

    // Estado de venta (idéntico al POS v1)
    const [reward, setReward] = useState<Reward | undefined>(undefined);
    const [productCategoryIdSelected, setProductCategoryIdSelected] = useState<string | undefined>(undefined);
    const [currentDish, setCurrentDish] = useState<Dish | undefined>(undefined);
    const [editingLine, setEditingLine] = useState<ShoppingCartLineV2 | undefined>(undefined);
    const [currentOrder, setCurrentOrder] = useState<Order | undefined>(undefined);
    const [savedOrder, setSavedOrder] = useState<SavedOrder | undefined>(undefined);
    const [tables, setTables] = useState<TableKiosk[]>([]);
    const [selectedTable, setSelectedTable] = useState<TableKiosk | undefined>(undefined);
    const [salesChannel, setSalesChannel] = useState<SalesChannel | undefined>(undefined);
    const [selectedOption, setSelectedOption] = useState<string>('');
    const [waitingForPayment, setWaitingForPayment] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    // El panel derecho muestra el pedido, la calculadora (ingresos y cobros) o el
    // registro de gastos: uno a la vez.
    const [sidePanel, setSidePanel] = useState<'order' | 'cashFlow' | 'expenses'>('order');

    // El CARRITO, el dinero y la aritmética que sale de ellos viven en su propio
    // hook, con tests (F4 del PLAN-REDISENO-PANEL-POS-Y-RECIBO): de esas cuentas
    // dependen los puntos y el corte ciego de caja. Se desestructura con los
    // MISMOS nombres que usaba la pantalla para que el resto del archivo no
    // tenga que cambiar. `tipAmountRef` es deliberado: el confirm de la propina
    // puede desembocar DIRECTO en crear la orden, antes de que React comprometa
    // el estado, así que el cobro lee el ref y no `tipAmount`.
    const cart = usePosCart();
    const {
        shoppingCart,
        setShoppingCart,
        rewardAmount,
        setRewardAmount,
        creditCardAmount,
        bankTransferAmount,
        cashRegisteredAmount,
        tipAmount,
        tipAmountRef,
        getSubtotal,
        getPromotion,
        getTotal,
        getTotalCash,
        getRemainingToCollect
    } = cart;

    /**
     * El bloque de cobro que se persiste: `payments[]`, con la caja y el cobrador
     * de cada renglón (los 3 escalares viejos se retiraron en la F6 del plan de
     * pagos). En un pedido "por pagar" `payments` queda vacío, que es la verdad:
     * todavía no se cobró nada.
     *
     * 🚨 `porPagar` se pasa EXPLÍCITO en cada camino y no se hereda del botón
     * "Por pagar" a ciegas: ese flag se quedaba pegado en `true` después de
     * cobrar una mesa, y "Completar pedido" sobre la siguiente mesa retomada
     * escribía los pagos en cero y cerraba el pedido sin cobro (dos casos
     * reales, 12 y 13-sep-2026, $165 y $164). Completar una mesa SIEMPRE cobra.
     */
    const buildPaymentBlock = (porPagar: boolean = waitingForPayment): Pick<Order, 'payments'> => ({
        payments: buildOrderPayments({
            cashAmount: porPagar ? 0 : getTotalCash(),
            creditCardAmount: porPagar ? 0 : creditCardAmount,
            bankTransferAmount: porPagar ? 0 : bankTransferAmount,
            date: new Date(),
            // Lo que el corte nunca tuvo: de qué caja y de quién es este dinero
            cashRegisterId: cashRegister?.id,
            collaboratorId: loggedUser?.uid
        })
    });

    // Modals
    const modalDishInfo = useOpenModal();
    const modalGetCustomer = useOpenModal();
    const modalAddPromotionalCode = useOpenModal();
    const modalAddShippingCost = useOpenModal();
    const modalAddOrderNote = useOpenModal();
    const modalOpenCashRegister = useOpenModal();
    const modalShowTicket = useOpenModal();
    const modalListSavedOrders = useOpenModal();
    // Rediseño QA 2026-08-26 (2ª ronda): DOS pasos separados, como pidió el
    // negocio — primero la PROPINA (botón propio, en espera del cliente) y
    // después el PAGO (la calculadora de siempre + selector de tipo, con los
    // pagos descontándose en el resumen del pedido).
    const modalPago = useOpenModal();

    // El ESPEJO del POS (`pos-sessions` → pantalla /display de la web): sync del
    // carrito, identificación del cliente desde su pantalla y la propina que él
    // elige. Todo fire-and-forget: la venta nunca depende del espejo.
    // ⚠️ Se monta UNA sola vez: el debounce y el `lastSessionId` viven a nivel
    // módulo en `PosSessionServ`, que da por hecho un solo POS montado.
    const mirror = usePosSessionMirror({
        cart,
        customerModalOpen: modalGetCustomer.modalOpen,
        openCustomerModal: modalGetCustomer.open,
        onAddCustomer: cart.setCustomer
    });

    // Índices y disponibilidad derivada (agotados por sucursal — §4.3)
    const recipesById = useMemo(() => {
        const map = new Map<string, Recipe>();
        recipes.forEach((recipe) => recipe.id && map.set(recipe.id, recipe));
        return map;
    }, [recipes]);
    const inputsById = useMemo(() => {
        const map = new Map<string, Input>();
        inputs.forEach((input) => input.id && map.set(input.id, input));
        return map;
    }, [inputs]);
    const modifierGroupsById = useMemo(() => {
        const map = new Map<string, ModifierGroup>();
        modifierGroups.forEach((group) => group.id && map.set(group.id, group));
        return map;
    }, [modifierGroups]);
    const availability = useMemo(
        () => getCatalogAvailability({ recipes, modifierGroups, dishes }, new Set(unavailableInputIds)),
        [recipes, modifierGroups, dishes, unavailableInputIds]
    );

    const onClean = () => {
        setProductCategoryIdSelected(undefined);
        setCurrentDish(undefined);
        setEditingLine(undefined);
        setCurrentOrder(undefined);
        setSavedOrder(undefined);
        // Carrito, dinero y propina de vuelta a cero (incluido el ref de la propina)
        cart.reset();
        // Siempre a `false`: aquí `selectedTable` todavía es la mesa del pedido
        // que ACABA de cerrarse (se limpia dos líneas abajo), así que
        // `!!selectedTable` dejaba el flag pegado en `true` tras cada mesa.
        setWaitingForPayment(false);
        setSidePanel('order');
        setSelectedTable(undefined);
        setIsEditing(false);
    };

    // Estaciona el carrito v2 en `saved-orders` (schemaVersion: 2 — el POS v1 no lo lista)
    const onSaveOrder = async () => {
        showLoader();
        try {
            if (shoppingCart && shoppingCart.products && shoppingCart.products.length > 0) {
                await savedOrdersService.saveSavedOrder(shoppingCart, savedOrder?.id, salesChannel, 2);
                showAlertMessage('Orden guardada exitosamente');
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onTakeSavedOrder = (order: SavedOrder) => {
        setSavedOrder(order);
        setShoppingCart(order.shoppingCart as ShoppingCartV2);
        setSalesChannel(order.salesChannel);
    };

    const onOpenCashRegister = async (amount: number) => {
        showLoader();
        try {
            const cashRegisterData = await cashRegisterService.openCashRegister(amount);
            dispatch(setCashRegister(cashRegisterData));
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const getPointsList = (points: number) => {
        const list = [{ points: 0, value: 0 }];
        if (reward && points > reward.reward.costPoints) {
            const quantity = Math.floor(points / reward.reward.costPoints);
            for (let i = 0; i < quantity; i++) {
                list.push({
                    points: reward.reward.costPoints * (i + 1),
                    value: reward.reward.value * (i + 1)
                });
            }
        }
        return list;
    };

    // Paso 1 del pedido (D6 del plan espejo): al caer el PRIMER platillo en un
    // carrito nuevo se ofrece identificar al cliente por teléfono, con "Omitir".
    // Solo una vez por pedido, y nunca sobre pedidos retomados/en edición (su
    // cliente ya está decidido) ni cuando ya hay cliente registrado.
    const customerPromptShownRef = useRef<boolean>(false);
    useEffect(() => {
        const lines = shoppingCart?.products?.length || 0;
        if (lines === 0) {
            customerPromptShownRef.current = false;
            return;
        }
        if (customerPromptShownRef.current) return;
        customerPromptShownRef.current = true;
        if (currentOrder || savedOrder || shoppingCart?.customer?.uid) return;
        modalGetCustomer.open();
    }, [shoppingCart?.products?.length]);

    // ----- Carrito -----
    const onAddLine = (line: ShoppingCartLineV2) => {
        cart.addLine(line);
        setCurrentDish(undefined);
        setEditingLine(undefined);
    };

    const onRemoveLine = cart.removeLine;

    const onShowDish = (dish: Dish) => {
        setCurrentDish(dish);
        setEditingLine(undefined);
        modalDishInfo.toggle();
    };

    const onEditLine = (line: ShoppingCartLineV2) => {
        const dish = dishes.find((d) => d.id === line.product.id);
        if (!dish) {
            showAlertError('El producto de esta línea ya no existe en el catálogo');
            return;
        }
        setCurrentDish(dish);
        setEditingLine(line);
        modalDishInfo.toggle();
    };

    const onAddCustomer = cart.setCustomer;

    // Quitar al cliente del pedido. ⚠️ Resetea los puntos SIEMPRE: el canje era de
    // ESE cliente y dejarlo cobraría un descuento de puntos que ya no le pertenece
    // a nadie. Se deja un walk-in en blanco (no `undefined`) para que el campo de
    // nombre siga ahí y el cajero pueda teclear a quién se le vende.
    //
    // Con confirmación: se lleva los puntos consigo y volver a ponerlo es buscarlo
    // otra vez por teléfono.
    const onRemoveCustomer = () => {
        const usingPoints = rewardAmount > 0;
        showIOSAlert({
            title: '¿Quitar al cliente del pedido?',
            message: usingPoints
                ? `Se quitará también el canje de $${rewardAmount.toFixed(
                      2
                  )} en puntos, porque esos puntos son de este cliente.`
                : 'El pedido queda sin cliente; puedes escribir un nombre o volver a buscarlo por teléfono.',
            buttons: [
                { text: 'Cancelar', onPress: () => {}, style: 'cancel' },
                {
                    text: 'Quitar cliente',
                    style: 'destructive',
                    onPress: cart.clearCustomer
                }
            ]
        });
    };

    // El bote de basura de la barra de arriba BORRA el pedido completo: un
    // mis-tap costaba todo el carrito sin preguntar nada (pedido de Irving).
    const onConfirmClean = () => {
        const lines = shoppingCart?.products?.length || 0;
        if (lines === 0) {
            onClean();
            return;
        }
        showIOSAlert({
            title: '¿Borrar el pedido?',
            message: `Se descartan ${lines} producto${
                lines === 1 ? '' : 's'
            } y todo lo capturado (cliente, nota, promoción, envío y puntos). Esto no se puede deshacer.`,
            buttons: [
                { text: 'Cancelar', onPress: () => {}, style: 'cancel' },
                { text: 'Borrar pedido', style: 'destructive', onPress: onClean }
            ]
        });
    };

    const onAddShippingCost = (amount: number) => {
        modalAddShippingCost.toggle();
        cart.setShippingCost(amount);
    };

    const onAddPromotionalCode = cart.applyPromotionalCode;

    // ----- Órdenes (misma colección `orders`, líneas v2) -----
    const onCompleteOrderTable = async () => {
        showLoader();
        try {
            // Cobrar una mesa mueve dinero (`payments[]` + cash-flow en completeOrder):
            // sin caja abierta ese efectivo cae fuera de toda ventana y el panel de
            // Cajas lo reporta como "vendió sin caja abierta". Faltaba este guard.
            if (!isActiveCashRegister(cashRegister, branchOfficeSelected?.id)) {
                modalOpenCashRegister.toggle();
                hideLoader();
                return;
            }
            if (currentOrder?.id) {
                // Completar una mesa ES cobrarla: el bloque de pago y la propina se
                // arman con lo que hay en el carrito, sin mirar el botón "Por
                // pagar" (que no aplica a una mesa que se está cerrando).
                const newOrder = {
                    ...currentOrder,
                    status: OrderStatus.WaitingForPayment,
                    subtotal: getSubtotal(),
                    promotion: getPromotion(),
                    shippingCost: shoppingCart?.shippingCost || 0,
                    total: getTotal(),
                    pointsAmount: rewardAmount,
                    // D2: la propina NO entra a total; los montos de pago SÍ la traen
                    tipAmount: tipAmountRef.current,
                    ...buildPaymentBlock(false),
                    createdWith: POS_CREATED_WITH,
                    lastUpdate: new Date()
                };
                // El gate va sobre la función que EJECUTA el cierre, no solo sobre
                // el botón: si por cualquier camino se llega aquí debiendo dinero,
                // se abre el cobro en vez de cerrar sin registro.
                if (needsPaymentBeforeComplete(newOrder as Order)) {
                    modalPago.open();
                    hideLoader();
                    return;
                }
                // `currentOrder` = el pedido ANTES de esta edición: con él la
                // bitácora registra QUÉ cambió, no solo que se tocó.
                await ordersService.updateOrder(newOrder, currentOrder);
                await ordersService.completeOrder(currentOrder.id);
                // Espejo POS: la venta cerrada, para que la pantalla del cliente
                // muestre el folio con su "¡gracias!"
                mirror.registerLastOrder({
                    id: currentOrder.id,
                    shortId: newOrder.shortId,
                    status: OrderStatus.Completed,
                    total: newOrder.total,
                    tipAmount: newOrder.tipAmount || 0
                });
                setCurrentOrder({ ...newOrder, status: OrderStatus.Completed });
                onClean();
                showAlertMessage('Orden completada exitosamente');
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    // ----- Paso 2: PAGO (la calculadora + selector de tipo) -----
    // PAGO LIBRE: efectivo, tarjeta y transferencia se registran en cualquier
    // orden y combinación, y se DESCUENTAN en el resumen del pedido
    // (OrderTotals, con sus X para quitarlos); el pedido se ingresa cuando el
    // restante llega a 0 (o con el efectivo final, que da cambio).
    const onRegisterPayment = cart.registerPayment;

    const onConfirmCompleteOrderTable = async () => {
        if (getRemainingToCollect() < 0) {
            showAlertError('Los pagos registrados superan el total — quítalos del resumen del pedido');
            return;
        }
        // Sin `waitingForPayment` en la condición: con el flag pegado en `true`
        // este `if` se saltaba el ModalPago y la mesa se cerraba sin cobro.
        if (getRemainingToCollect() > 0) {
            modalPago.open();
            return;
        }
        await onCompleteOrderTable();
    };

    const onUpdateOrder = async () => {
        showLoader();
        try {
            if (currentOrder?.id && shoppingCart?.products && shoppingCart.customer) {
                const updatedOrder: Order = {
                    ...currentOrder,
                    // El `id` local del carrito no se persiste (igual que en el POS v1)
                    products: shoppingCart.products.map(({ id, ...line }) => line) as any,
                    customer: shoppingCart.customer,
                    promotionalCode: shoppingCart.promotionalCode,
                    salesChannel: salesChannel,
                    tableKiosk: selectedTable,
                    note: shoppingCart.note || '',
                    subtotal: getSubtotal(),
                    promotion: getPromotion(),
                    shippingCost: shoppingCart.shippingCost || 0,
                    total: getTotal(),
                    pointsAmount: rewardAmount,
                    // "Guardar" una mesa en preparación NO la cobra: pagos en cero,
                    // explícito, aunque alguien haya tocado "Por pagar".
                    ...buildPaymentBlock(true),
                    createdWith: POS_CREATED_WITH,
                    lastUpdate: new Date()
                };
                await ordersService.updateOrder(updatedOrder, currentOrder);
                onClean();
                showAlertMessage('Orden actualizada exitosamente');
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onCreateOrder = async () => {
        showLoader();
        try {
            if (!isActiveCashRegister(cashRegister, branchOfficeSelected?.id)) {
                modalOpenCashRegister.toggle();
                hideLoader();
                return;
            }
            // Antes este `if` fallaba EN SILENCIO: sin cliente el botón no hacía
            // nada y no decía por qué (pasa al darle "Omitir" al modal de cliente).
            if (!shoppingCart?.customer) {
                showAlertError('Agrega el cliente del pedido (el chip "Cliente" del resumen)');
                hideLoader();
                return;
            }
            if (loggedUser && branchOfficeSelected?.id && shoppingCart?.products && shoppingCart.customer) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const branchOffice = await branchOfficesService.getBranchOffice(branchOfficeSelected.id);
                // Folio {sucursal(3)}{ddMMyy}-{seq(5)} con seq ATÓMICO (dos POS o
                // POS + web al mismo tiempo ya no repiten folio). Semilla y guardia
                // = folio máximo del día con UNA lectura (antes se cargaban todas
                // las órdenes del día en cada venta).
                const shortId = await reserveShortId({
                    scope: 'orders',
                    branchOfficeId: branchOfficeSelected.id,
                    branchNumber: branchOffice.number,
                    date: today
                });
                const order: Order = {
                    id: currentOrder?.id,
                    shortId: currentOrder?.shortId || shortId,
                    status: selectedTable
                        ? OrderStatus.Preparing
                        : waitingForPayment
                        ? OrderStatus.WaitingForPayment
                        : OrderStatus.Paid,
                    products: shoppingCart.products.map(({ id, ...line }) => line) as any,
                    customer: shoppingCart.customer,
                    branchOfficeId: branchOfficeSelected.id,
                    collaboratorId: loggedUser.uid,
                    promotionalCode: shoppingCart.promotionalCode,
                    salesChannel: salesChannel,
                    note: shoppingCart.note || '',
                    subtotal: getSubtotal(),
                    promotion: getPromotion(),
                    shippingCost: shoppingCart.shippingCost || 0,
                    total: getTotal(),
                    pointsAmount: rewardAmount,
                    // D2: la propina NO entra a total; los montos de pago SÍ la traen
                    tipAmount: waitingForPayment ? 0 : tipAmountRef.current,
                    ...buildPaymentBlock(),
                    createdWith: POS_CREATED_WITH,
                    date: new Date(),
                    lastUpdate: new Date(),
                    duration: 0,
                    tableKiosk: selectedTable
                };
                order.products = order.products.filter((line) => line !== undefined);
                await ordersService.saveOrder(order);
                // Espejo POS: folio y estado de la venta recién creada — la
                // pantalla del cliente muestra "¡gracias!" o "pendiente de pago"
                mirror.registerLastOrder({
                    id: order.id || '',
                    shortId: order.shortId,
                    status: order.status,
                    total: order.total,
                    tipAmount: order.tipAmount || 0
                });
                if (savedOrder?.id) {
                    await savedOrdersService.deleteSavedOrder(savedOrder.id);
                }
                onClean();
                showAlertMessage('Orden creada exitosamente');
                // La orden entra sola al tablero de /orders, que es lo que ve cocina.
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onConfirmCreateOrder = async () => {
        if (getRemainingToCollect() < 0) {
            showAlertError('Los pagos registrados superan el total — quítalos del resumen del pedido');
            return;
        }
        if (!waitingForPayment && getRemainingToCollect() > 0) {
            modalPago.open();
            return;
        }
        await onCreateOrder();
    };

    // ----- Carga -----
    const getData = async () => {
        showLoader();
        try {
            // Catálogo con caché por versión (1 lectura si no cambió; antes ~340
            // docs en cada montaje del POS — ver @api/Catalog/CatalogCache).
            const [catalog, dataRewards] = await Promise.all([getPosCatalog(), rewardsService.getRewards()]);
            const {
                dishes: dataDishes,
                recipes: dataRecipes,
                modifierGroups: dataGroups,
                inputs: dataInputs,
                productCategories: dataCategories
            } = catalog;
            // Solo lo vendible en ESTA sucursal (misma regla que el menú v1)
            setDishes(
                dataDishes.filter(
                    (dish) =>
                        dish.status &&
                        (dish.branchOfficesAvailable?.includes('all') ||
                            (branchOfficeSelected?.id
                                ? dish.branchOfficesAvailable?.includes(branchOfficeSelected.id)
                                : false))
                )
            );
            setRecipes(dataRecipes);
            setModifierGroups(dataGroups);
            setInputs(dataInputs);
            setCategories(dataCategories);
            setReward(dataRewards);

            if (branchOfficeSelected?.id) {
                const [tablesData, branchOffice] = await Promise.all([
                    tableKioskService.getTables(branchOfficeSelected.id),
                    branchOfficesService.getBranchOffice(branchOfficeSelected.id)
                ]);
                setTables(tablesData);
                setUnavailableInputIds(branchOffice.unavailableInputs || []);
            }
            setSalesChannel(salesChannels[0] || undefined);
            cart.setCustomerName(`0000`);
            if (!isActiveCashRegister(cashRegister, branchOfficeSelected?.id)) {
                modalOpenCashRegister.toggle();
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    // Retomar un pedido tiene que devolver la cuenta COMPLETA, no solo las
    // líneas (bug reportado 2026-08-26): se restauraban productos/cliente/nota
    // pero NO los puntos, la promoción ni el envío, así que `getTotal()`
    // volvía a ser el subtotal — de ahí salía el % de propina sobre el
    // subtotal, y al cobrar se pisaba `pointsAmount`/`promotion` con 0 (el
    // cliente pagaba completo el descuento que ya había aplicado).
    const getOrder = async (orderId: string) => {
        showLoader();
        try {
            const orderData = await ordersService.getOrder(orderId);
            setCurrentOrder(orderData);
            setSalesChannel(orderData.salesChannel);
            const table = isTableOrder(orderData) ? orderData.tableKiosk : undefined;
            setSelectedTable(table);
            // El selector de canal/mesa se pintaba vacío al retomar
            if (table?.id) setSelectedOption(`table-${table.id}`);
            else if (orderData.salesChannel?.id) setSelectedOption(`channel-${orderData.salesChannel.id}`);
            // El flag "Por pagar" se fija según el pedido que se retoma y NUNCA se
            // hereda del anterior: una mesa en preparación se edita y se guarda;
            // una mesa lista se cobra; un pedido de mostrador pendiente sigue
            // pendiente hasta que el cajero lo diga.
            const mesaEnPreparacion = isTableOrder(orderData) && orderData.status === OrderStatus.Preparing;
            setIsEditing(mesaEnPreparacion);
            setWaitingForPayment(
                isTableOrder(orderData) ? mesaEnPreparacion : orderData.status === OrderStatus.WaitingForPayment
            );
            // Propina: en MOSTRADOR sigue viniendo solo del espejo (el cliente la
            // confirma en su pantalla) y una orden "por pagar" se guarda con
            // tipAmount 0 por diseño, así que no hay nada que restaurar. En MESA
            // sí: desde F4 el cliente la deja desde su teléfono al pedir la
            // cuenta, y si no se restaura aquí el cajero cobra sin ella.
            cart.restore({
                tipAmount: isTableOrder(orderData) ? orderData.tipAmount || 0 : undefined,
                products: (orderData.products as any as ShoppingCartLineV2[]).map((line, index) => ({
                    ...line,
                    id: index
                })),
                customer: orderData.customer,
                note: orderData.note,
                promotionalCode: orderData.promotionalCode,
                shippingCost: orderData.shippingCost || undefined,
                pointsAmount: orderData.pointsAmount || 0
            });
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        const state = location.state as { orderId: string } | undefined;
        const load = async () => {
            await getData();
            if (state?.orderId) {
                await getOrder(state.orderId);
            }
        };
        void load();
    }, [location.state]);

    // CANDADO DE CAJA: mientras no haya caja activa para la sucursal en operación,
    // el modal se mantiene abierto y no se puede descartar. `getData` ya lo abría al
    // montar, pero eso dejaba dos huecos: descartarlo y seguir armando el carrito, y
    // quedarse sin caja tras cerrarla desde otro dispositivo (la barra revalida
    // contra Firestore y la puede quitar de Redux).
    useEffect(() => {
        if (!isActiveCashRegister(cashRegister, branchOfficeSelected?.id)) {
            modalOpenCashRegister.open();
        }
    }, [cashRegister, branchOfficeSelected?.id]);

    useEffect(() => {
        if (selectedTable) {
            setSelectedOption(`table-${selectedTable.id}`);
        } else if (salesChannel) {
            setSelectedOption(`channel-${salesChannel.id}`);
        } else {
            setSelectedOption('');
        }
    }, [selectedTable, salesChannel]);

    const handleChannelTableChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (value === '') {
            setSelectedTable(undefined);
            setSalesChannel(undefined);
        } else if (value.startsWith('table-')) {
            const table = tables.find((t) => t.id === value.replace('table-', ''));
            setSelectedTable(table);
            setSalesChannel(undefined);
            setWaitingForPayment(true);
        } else {
            const channel = salesChannels.find((c) => c.id === value.replace('channel-', ''));
            setSalesChannel(channel);
            setSelectedTable(undefined);
            setWaitingForPayment(false);
        }
    };

    return (
        <Stack height="100%">
            <Grid container height="100%">
                <Grid
                    item
                    xs={12}
                    md={7}
                    maxHeight="100%"
                    overflow="auto"
                    sx={{ borderRight: '2px solid', borderColor: 'divider' }}
                >
                    <ProductCategoriesGrid
                        enabled={true}
                        productCategoryIdSelected={productCategoryIdSelected}
                        onSelectProductCategoryId={setProductCategoryIdSelected}
                    />
                    <DishesGrid
                        dishes={dishes}
                        categories={categories}
                        productCategoryIdSelected={productCategoryIdSelected}
                        unavailableDishIds={availability.unavailableDishIds}
                        onClickDish={onShowDish}
                    />
                </Grid>
                {/* PANEL DERECHO — 3 zonas (PLAN-REDISENO-PANEL-POS-Y-RECIBO, D2).
                    Antes el contenido pedía `height="90%"` teniendo la barra arriba
                    (48 px + 1 px + 90% > 100%) y sus hijos flex no declaraban
                    `minHeight: 0`, asi que el carrito NO podia encogerse: en vez de
                    scrollear empujaba, y lo que se salia por abajo eran el resumen y
                    los botones de cobro. Regla: los botones de cobro NUNCA se salen
                    de la pantalla, con 1 producto o con 30. */}
                <PosOrderPanel
                    onToggleSavedOrders={() => modalListSavedOrders.toggle()}
                    onToggleCashFlow={() => setSidePanel(sidePanel === 'cashFlow' ? 'order' : 'cashFlow')}
                    onToggleExpenses={() => setSidePanel(sidePanel === 'expenses' ? 'order' : 'expenses')}
                    onSaveOrder={onSaveOrder}
                    onClean={onConfirmClean}
                    onShowTicket={() => modalShowTicket.toggle()}
                    showCashFlowCalculator={sidePanel === 'cashFlow'}
                    showExpenseRegister={sidePanel === 'expenses'}
                    shoppingCart={shoppingCart}
                    currentOrder={currentOrder}
                    isEditing={isEditing}
                    selectedTable={selectedTable}
                    onEditLine={onEditLine}
                    onRemoveLine={onRemoveLine}
                    onToggleGetCustomer={() => modalGetCustomer.toggle()}
                    onToggleAddNote={() => modalAddOrderNote.toggle()}
                    onTogglePromo={() => modalAddPromotionalCode.toggle()}
                    onAddShipping={() => modalAddShippingCost.toggle()}
                    getSubtotal={getSubtotal}
                    getPromotion={getPromotion}
                    getTotal={getTotal}
                    getTotalCash={getTotalCash}
                    rewardAmount={rewardAmount}
                    tipAmount={tipAmount}
                    creditCardAmount={creditCardAmount}
                    bankTransferAmount={bankTransferAmount}
                    cashRegisteredAmount={cashRegisteredAmount}
                    onRemoveCreditCard={cart.removeCreditCard}
                    onRemoveBankTransfer={cart.removeBankTransfer}
                    onRemoveCashRegistered={cart.removeCashRegistered}
                    onRemoveTip={mirror.onRemoveTip}
                    onRemovePoints={cart.removePoints}
                    onRemovePromo={cart.removePromotionalCode}
                    onRemoveShipping={() => cart.setShippingCost(undefined)}
                    onRemoveCustomer={onRemoveCustomer}
                    onCustomerNameChange={(e) => cart.setCustomerName(e.target.value)}
                    setRewardAmount={setRewardAmount}
                    getPointsList={getPointsList}
                    onEditNote={() => modalAddOrderNote.toggle()}
                    onRemoveNote={() => cart.setNote(undefined)}
                    salesChannels={salesChannels}
                    tables={tables}
                    selectedOption={selectedOption}
                    onSelectOptionChange={handleChannelTableChange}
                    waitingForPayment={waitingForPayment}
                    setWaitingForPayment={setWaitingForPayment}
                    onConfirmCreate={onConfirmCreateOrder}
                    onConfirmComplete={onConfirmCompleteOrderTable}
                    onConfirmUpdate={onUpdateOrder}
                />
            </Grid>

            <PosModals
                modalDishInfo={modalDishInfo}
                currentDish={currentDish}
                editingLine={editingLine}
                modifierGroupsById={modifierGroupsById}
                recipesById={recipesById}
                inputsById={inputsById}
                unavailableRecipeIds={availability.unavailableRecipeIds}
                onAddLine={onAddLine}
                modalGetCustomer={modalGetCustomer}
                onAddCustomer={onAddCustomer}
                identifyPrefillPhone={mirror.identifyPrefillPhone}
                onRequestIdentifyOnScreen={mirror.requestIdentify}
                modalAddPromotionalCode={modalAddPromotionalCode}
                onAddPromotionalCode={onAddPromotionalCode}
                modalAddShippingCost={modalAddShippingCost}
                onAddShippingCost={onAddShippingCost}
                modalAddOrderNote={modalAddOrderNote}
                onAddOrderNote={cart.setNote}
                modalPago={modalPago}
                pagoRemaining={Math.max(0, getRemainingToCollect())}
                salesChannel={salesChannel}
                onRegisterPayment={onRegisterPayment}
                onContinuePago={isTableOrder(currentOrder) ? onCompleteOrderTable : onCreateOrder}
                modalOpenCashRegister={modalOpenCashRegister}
                onOpenCashRegister={onOpenCashRegister}
                cashRegisterMandatory={!isActiveCashRegister(cashRegister, branchOfficeSelected?.id)}
                cashRegisterBranchOfficeId={branchOfficeSelected?.id}
                onExitCashRegister={() => navigation('../orders')}
                modalListSavedOrders={modalListSavedOrders}
                onTakeSavedOrder={onTakeSavedOrder}
                modalShowTicket={modalShowTicket}
                ticket={{
                    customer: shoppingCart?.customer,
                    products: shoppingCart?.products || [],
                    subtotal: getSubtotal(),
                    promotion: getPromotion(),
                    shippingCost: shoppingCart?.shippingCost || 0,
                    points: rewardAmount,
                    total: getTotal(),
                    tipAmount: tipAmount,
                    branchOfficeName: branchOfficeSelected?.name,
                    channelLabel: selectedTable?.name || salesChannel?.name
                }}
            />
        </Stack>
    );
};

export default POSScreen;
